import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, ChevronLeft, ChevronRight, Send } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { api, mediaUrl } from "@/lib/api";
import { STORY_GRADIENTS, STORY_EMOJIS } from "@/lib/storyConfig";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { Input } from "@/components/ui/input";

const DURATION = 5000;

export default function StoryViewer({ groups, startIndex, onClose }) {
  const { user } = useAuth();
  const [gi, setGi] = useState(startIndex);
  const [si, setSi] = useState(0);
  const [progress, setProgress] = useState(0);
  const [reply, setReply] = useState("");
  const [paused, setPaused] = useState(false);
  const [flyEmoji, setFlyEmoji] = useState(null);
  const timerRef = useRef(null);
  const startRef = useRef(Date.now());
  const elapsedRef = useRef(0);

  const group = groups[gi];
  const story = group?.stories[si];
  const isOwn = group?.user.user_id === user.user_id;

  const nextStory = useCallback(() => {
    setProgress(0);
    elapsedRef.current = 0;
    startRef.current = Date.now();
    if (si < group.stories.length - 1) setSi((s) => s + 1);
    else if (gi < groups.length - 1) { setGi((g) => g + 1); setSi(0); }
    else onClose();
  }, [si, gi, group, groups, onClose]);

  const prevStory = () => {
    setProgress(0);
    elapsedRef.current = 0;
    startRef.current = Date.now();
    if (si > 0) setSi((s) => s - 1);
    else if (gi > 0) { setGi((g) => g - 1); setSi(0); }
  };

  useEffect(() => {
    if (story?.id) api.post(`/stories/${story.id}/view`).catch(() => {});
  }, [story?.id]);

  useEffect(() => {
    startRef.current = Date.now();
    elapsedRef.current = 0;
    setProgress(0);
    timerRef.current = setInterval(() => {
      if (paused) { startRef.current = Date.now() - elapsedRef.current; return; }
      elapsedRef.current = Date.now() - startRef.current;
      const pct = Math.min(100, (elapsedRef.current / DURATION) * 100);
      setProgress(pct);
      if (pct >= 100) nextStory();
    }, 50);
    return () => clearInterval(timerRef.current);
  }, [gi, si, nextStory, paused]);

  const sendReaction = async (emoji) => {
    setFlyEmoji({ emoji, key: Date.now() });
    setTimeout(() => setFlyEmoji(null), 1000);
    try { await api.post(`/stories/${story.id}/react`, { emoji }); } catch {}
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    const content = `↩️ Replied to your story: ${reply}`;
    setReply("");
    try {
      await api.post(`/messages/${group.user.user_id}`, { content });
      toast.success("Reply sent");
    } catch {
      toast.error("Could not send reply");
    }
  };

  if (!group || !story) return null;

  return (
    <div data-testid="story-viewer" className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center" onClick={onClose}>
      <button data-testid="story-close-btn" onClick={onClose} className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center z-20">
        <X className="w-6 h-6" />
      </button>

      <button onClick={(e) => { e.stopPropagation(); prevStory(); }} className="absolute left-4 md:left-10 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white items-center justify-center hidden md:flex z-20">
        <ChevronLeft className="w-6 h-6" />
      </button>
      <button onClick={(e) => { e.stopPropagation(); nextStory(); }} className="absolute right-4 md:right-10 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white items-center justify-center hidden md:flex z-20">
        <ChevronRight className="w-6 h-6" />
      </button>

      <motion.div
        key={`${gi}-${si}`}
        initial={{ scale: 0.96, opacity: 0.6 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-[420px] h-[82vh] mx-4 rounded-2xl overflow-hidden bg-[#140A24] border border-[#2D1B48]"
      >
        <div className="absolute top-2 left-2 right-2 flex gap-1 z-10">
          {group.stories.map((_, idx) => (
            <div key={idx} className="flex-1 h-1 rounded-full bg-white/30 overflow-hidden">
              <div className="h-full bg-white transition-none" style={{ width: idx < si ? "100%" : idx === si ? `${progress}%` : "0%" }} />
            </div>
          ))}
        </div>

        <div className="absolute top-6 left-3 right-3 flex items-center gap-2 z-10">
          <Avatar user={group.user} size={36} />
          <div className="text-white">
            <div className="text-sm font-semibold flex items-center gap-1">{group.user.name}{group.user.verified && <VerifiedBadge size={13} />}</div>
            <div className="text-xs text-white/70">{formatDistanceToNow(new Date(story.created_at), { addSuffix: true })}</div>
          </div>
        </div>

        {story.kind === "text" ? (
          <div className="w-full h-full flex items-center justify-center p-8" style={{ background: STORY_GRADIENTS[story.background] || STORY_GRADIENTS.grad1 }}>
            <p data-testid="story-text-content" className="text-white text-center font-heading text-3xl font-bold leading-snug break-words">{story.text}</p>
          </div>
        ) : (
          <img src={mediaUrl(story.image_url)} alt="" className="w-full h-full object-cover" />
        )}

        {/* flying emoji */}
        <AnimatePresence>
          {flyEmoji && (
            <motion.div
              key={flyEmoji.key}
              initial={{ y: 0, opacity: 1, scale: 1 }}
              animate={{ y: -220, opacity: 0, scale: 1.8 }}
              transition={{ duration: 1 }}
              className="absolute bottom-24 left-1/2 -translate-x-1/2 text-5xl pointer-events-none z-20"
            >
              {flyEmoji.emoji}
            </motion.div>
          )}
        </AnimatePresence>

        {/* tap zones */}
        <div className="absolute inset-y-0 left-0 w-1/3 top-16 bottom-24" onClick={(e) => { e.stopPropagation(); prevStory(); }} />
        <div className="absolute inset-y-0 right-0 w-1/3 top-16 bottom-24" onClick={(e) => { e.stopPropagation(); nextStory(); }} />

        {/* reactions + reply (only for others' stories) */}
        {!isOwn && (
          <div className="absolute bottom-0 left-0 right-0 p-3 z-20 bg-gradient-to-t from-black/70 to-transparent" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-center gap-1.5 mb-2">
              {STORY_EMOJIS.map((em) => (
                <button
                  key={em}
                  data-testid={`story-emoji-${em}`}
                  onClick={() => sendReaction(em)}
                  className="text-2xl hover:scale-125 transition-transform"
                >
                  {em}
                </button>
              ))}
            </div>
            <form onSubmit={sendReply} className="flex gap-2" onFocus={() => setPaused(true)} onBlur={() => setPaused(false)}>
              <Input
                data-testid="story-reply-input"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder={`Reply to ${group.user.name?.split(" ")[0]}...`}
                className="rounded-full bg-white/15 border-white/20 text-white placeholder-white/60 h-10"
              />
              <button data-testid="story-reply-send" type="submit" className="w-10 h-10 shrink-0 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center">
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        )}
        {isOwn && (
          <div className="absolute bottom-4 left-0 right-0 text-center text-white/70 text-xs z-20">
            {story.reaction_count > 0 ? `${story.reaction_count} reaction${story.reaction_count > 1 ? "s" : ""}` : "Your story"}
          </div>
        )}
      </motion.div>
    </div>
  );
}
